import { Link } from "@tanstack/react-router";
import { Card, type CardProps, Flex, List } from "antd";
import type React from "react";
import { CustomEmpty } from "./base";

interface CustomListProps<T> {
	title?: string;
	data: T[];
	renderItem: (item: T, index: number) => React.ReactNode;
	/** Optional leading node per row (e.g. an icon), rendered in its own aligned column. */
	renderLeading?: (item: T, index: number) => React.ReactNode;
	/** Optional trailing node per row (e.g. a tag or icon), right-aligned in its own column. */
	renderTrailing?: (item: T, index: number) => React.ReactNode;
	/** Makes the whole row clickable (pointer cursor included). */
	onItemClick?: (item: T) => void;
	seeAllTo?: string;
	cardProps?: Omit<CardProps, "title" | "extra" | "size">;
	/** Empty-state noun ("No <emptyText>."); defaults to the title. */
	emptyText?: string;
	/** When set, the empty state shows an "Add" link that fires this (e.g. opens a create dialog). */
	onAdd?: () => void;
}

/**
 * A titled Card wrapping an antd List with optional multi-column grid layout and a "See all" link.
 * Minimum 1 column; defaults to 1.
 */
export function CustomList<T>({
	title,
	data,
	renderItem,
	renderLeading,
	renderTrailing,
	onItemClick,
	seeAllTo,
	cardProps,
	emptyText,
	onAdd,
}: CustomListProps<T>) {
	return (
		<>
			{data.length > 0 && (
				<Card
					title={title}
					size="small"
					extra={seeAllTo ? <Link to={seeAllTo}>View all</Link> : undefined}
					style={{ width: "100%" }}
					{...cardProps}
				>
					<List
						size="small"
						dataSource={data}
						renderItem={(item, index) => (
							// Card body already insets the list; the item's own horizontal padding
							// would double it.
							<List.Item
								style={{
									paddingInline: 0,
									cursor: onItemClick ? "pointer" : undefined,
								}}
								onClick={onItemClick && (() => onItemClick(item))}
							>
								{renderLeading || renderTrailing ? (
									<Flex
										gap={10}
										align="center"
										style={{ width: "100%", minWidth: 0 }}
									>
										{renderLeading && (
											<Flex style={{ flexShrink: 0, alignSelf: "flex-start" }}>
												{renderLeading(item, index)}
											</Flex>
										)}
										<div style={{ flex: 1, minWidth: 0 }}>
											{renderItem(item, index)}
										</div>
										{renderTrailing && (
											<Flex gap={8} align="center" style={{ flexShrink: 0 }}>
												{renderTrailing(item, index)}
											</Flex>
										)}
									</Flex>
								) : (
									renderItem(item, index)
								)}
							</List.Item>
						)}
					/>
				</Card>
			)}
			{data.length === 0 && (
				<CustomEmpty
					description={emptyText ?? title ?? "items"}
					onAdd={onAdd}
				/>
			)}
		</>
	);
}
